import React from 'react'
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  from,
  ApolloLink,
} from '@apollo/client'
import {
  getGraphQLErrorFingerprint,
  getSentryNetworkErrorContext,
  parseSeverityFromGqlErrors,
  useFirebaseAuth,
} from '@flock/utils'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { createUploadLink } from 'apollo-upload-client'
import * as Sentry from '@sentry/gatsby'
// @ts-ignore
import { Severity } from '@flock/utils/src/__generated__/protobuf/ts/errors/errors'
import {
  GqlBackendBadge,
  isValuationCutoverOp,
  resolveGqlBackend,
} from './gqlBackendOverride'

type ConfiguredApolloProviderProps = {
  children: React.ReactNode
}

type FirebaseUser = ReturnType<typeof useFirebaseAuth>['user']

type ApolloClientConfig = {
  clientName: 'legacy' | 'v2'
  useLegacySeverity?: boolean
  // ACQ-3120 canary override: when set, allowlisted valuation ops route to
  // this URI (v2 API) and everything else stays on `uri` — see
  // gqlBackendOverride.tsx
  v2SplitUri?: string
}

const EXPECTED_NOT_FOUND_OPS = new Set([
  'AdminAddressValuationsGetSingleFamilyAddressValuation',
  'AdminAddressValuationsGetMultiFamilyAddressValuation',
])

const createConfiguredApolloClient = (
  uri: string | undefined,
  user: FirebaseUser,
  { clientName, useLegacySeverity = true, v2SplitUri }: ApolloClientConfig
) => {
  // ops the split link sends to v2 — their Sentry captures must be tagged v2
  // and must skip legacy severity parsing (v2 errors carry no severity ext)
  const opGoesToV2 = (operationName: string | undefined) =>
    Boolean(v2SplitUri) && isValuationCutoverOp(operationName)

  const apolloLinks = []
  const errorLink = onError((errResult) => {
    const { operation, graphQLErrors, networkError } = errResult
    const context = operation.getContext()
    const traceId = context.response?.headers?.get('X-Amzn-Trace-Id')
    const hasErrors = Boolean(graphQLErrors?.length || networkError)
    const isV2Op = opGoesToV2(operation.operationName)
    // v2 has no severity ext; "no valuation yet" is an expected state for these
    // lookups and is handled by the query's onError
    const isExpectedNotFound =
      !networkError &&
      EXPECTED_NOT_FOUND_OPS.has(operation.operationName) &&
      Boolean(graphQLErrors?.length) &&
      graphQLErrors!.every((e) => e.message === 'record not found')
    const shouldCapture =
      hasErrors &&
      !isExpectedNotFound &&
      (isV2Op ||
        !useLegacySeverity ||
        parseSeverityFromGqlErrors(graphQLErrors) !== Severity.SEVERITY_IGNORE)

    if (shouldCapture) {
      const error =
        networkError instanceof Error
          ? networkError
          : new Error(`GQL Operation failed: ${operation.operationName}`)
      Sentry.captureException(error, {
        fingerprint: getGraphQLErrorFingerprint(operation.operationName),
        tags: {
          traceId,
          operationName: operation.operationName,
          clientName: isV2Op ? 'v2' : clientName,
        },
        extra: {
          variables: operation.variables,
          graphQLErrors,
          networkError: getSentryNetworkErrorContext(networkError),
          userName: user?.displayName,
          userEmail: user?.email,
        },
      })
    }
  })

  apolloLinks.push(errorLink)

  const authorizationLink = setContext(async (_, previousContext) => {
    // If this token will expire in less than an hour, refresh the token
    const token = await user?.getIdToken()
    return {
      headers: {
        ...previousContext.headers,
        Authorization: `Bearer ${token}`,
      },
    }
  })
  apolloLinks.push(authorizationLink)

  const uploadLink = createUploadLink({ uri })
  // route allowlisted valuation ops to v2 while the canary override is
  // active; every other operation keeps hitting the v1 uri
  const terminatingLink = v2SplitUri
    ? ApolloLink.split(
        (op) => isValuationCutoverOp(op.operationName),
        createUploadLink({ uri: v2SplitUri }) as unknown as ApolloLink,
        uploadLink as unknown as ApolloLink
      )
    : uploadLink
  apolloLinks.push(terminatingLink)

  return new ApolloClient({
    link: from(apolloLinks as ApolloLink[]),
    cache: new InMemoryCache(),
  })
}

export const useV2ApolloClient = () => {
  const { user } = useFirebaseAuth()
  return React.useMemo(
    () =>
      createConfiguredApolloClient(
        process.env.GATSBY_APOLLO_URL_V2 || process.env.GATSBY_APOLLO_URL,
        user,
        {
          clientName: 'v2',
          useLegacySeverity: false,
        }
      ),
    [user]
  )
}

const ConfiguredApolloProvider = ({
  children,
}: ConfiguredApolloProviderProps) => {
  const { user } = useFirebaseAuth()
  // ACQ-3120 canary override: ?gqlBackend=v2 routes this browser's valuation
  // ops to the v2 API ahead of the cutover; all other admin domains stay on
  // v1 (see gqlBackendOverride.tsx)
  const { v2SplitUri } = resolveGqlBackend()
  const client = React.useMemo(
    () =>
      createConfiguredApolloClient(process.env.GATSBY_APOLLO_URL, user, {
        clientName: 'legacy',
        v2SplitUri,
      }),
    [user, v2SplitUri]
  )

  return (
    <>
      <ApolloProvider client={client}>{children}</ApolloProvider>
      <GqlBackendBadge />
    </>
  )
}

export default ConfiguredApolloProvider
